/**
 * DashboardLayout.js
 *
 * Reusable layout component for rendering dashboard sections
 * Handles dynamic section rendering, empty states, and responsive layout
 */

import React from 'react';
import '../widgets/DashboardLayout.css';

/**
 * EmptyState Component
 * Displays when there's no data to show
 */
const EmptyState = ({ section, message }) => (
  <div className="dashboard-empty-state">
    <div className="empty-icon">📊</div>
    <h3>No {section || 'Data'}</h3>
    <p>{message || 'No data available for this section.'}</p>
  </div>
);

/**
 * Section Component
 * Wraps individual dashboard sections with consistent styling
 *
 * Props:
 * - id: Section identifier
 * - title: Section title (can be string or JSX)
 * - titleAction: Optional action element (e.g., refresh button) to display next to title
 * - children: Section content
 * - className: Additional CSS classes
 */
const Section = ({ id, title, titleAction, children, className = '' }) => {
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return null;
  }

  return (
    <section id={id} className={`dashboard-section ${className}`}>
      {title && (
        <div className="section-title-container">
          <h2 className="section-title">{title}</h2>
          {titleAction && <div className="section-title-action">{titleAction}</div>}
        </div>
      )}
      <div className="section-content">{children}</div>
    </section>
  );
};

/**
 * DashboardLayout Component
 *
 * Main layout component for dashboard sections
 *
 * Props:
 * - sections: Array of section objects
 *   Each section: { id, title, content, className }
 * - children: Alternative to sections prop - render custom content
 * - columns: Number of columns (1, 2, 3, or 'auto')
 * - gap: Grid gap size ('sm', 'md', 'lg')
 */
const DashboardLayout = ({
  sections = [],
  children,
  columns = 'auto',
  gap = 'md',
  className = ''
}) => {
  // If sections are provided, render them
  if (sections && sections.length > 0) {
    return (
      <div
        className={`dashboard-layout dashboard-columns-${columns} dashboard-gap-${gap} ${className}`}
      >
        {sections.map((section) => (
          <Section
            key={section.id || section.title}
            id={section.id}
            title={section.title}
            className={section.className}
          >
            {section.content}
          </Section>
        ))}
      </div>
    );
  }

  // If children are provided, render them
  if (children) {
    return (
      <div
        className={`dashboard-layout dashboard-columns-${columns} dashboard-gap-${gap} ${className}`}
      >
        {children}
      </div>
    );
  }

  // No content
  return <EmptyState section="Dashboard" message="No dashboard sections to display." />;
};

// Export helper components for use in dashboard implementations
export { Section, EmptyState };

export default DashboardLayout;
